import { AgTechRenderedPopUp, AgTechPopUpPosition } from '@agtech/core/functions/AgTechAppPopUps';
import { useClickable } from '../Buttons/AgTechButtons';
import { IAgTechGridItem } from '../Grids/Data/AgTechGridData';
import { AgTechGridState } from '../Grids/Data/AgTechGridState';
import { AgTechGridSearchProps, AgTechGridSearchState } from '../Grids/Features/AgTechGridSearching';
import { AgTechGridView, AgTechGridViewConfig, AgTechGridViewSelectionPopUp } from '../Grids/Features/AgTechGridViews';
import AgTechSearchControl from '../Widgets/AgTechSearchControl';
import React, { useRef } from 'react'

export type AgTechGridHeaderConfiguration = {
    title: string,
    content?: () => React.ReactNode,
    showCount?: boolean
}

export type AgTechGridHeaderSearchConfig<TItem> = {
    searchConfig?: AgTechGridSearchProps<TItem>,
    onSearchStateUpdated: (searchState: AgTechGridSearchState<TItem>) => void
}

export type AgTechGridHeaderViewsConfig<TItem extends IAgTechGridItem> = {
    views?: AgTechGridViewConfig<TItem>,
    onViewSelected: (selectedView: AgTechGridView<TItem>) => void
}

export type AgTechGridHeaderProps<TItem extends IAgTechGridItem, TContextData> = {
    gridState: AgTechGridState<TItem, TContextData>,
    header?: AgTechGridHeaderConfiguration,
    search?: AgTechGridHeaderSearchConfig<TItem>,
    views?: AgTechGridHeaderViewsConfig<TItem>,
    onCreate?: () => void
}

const AgTechGridHeader = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridHeaderProps<TItem, TContextData>
) => {
    return (
        <div
            className="column-based-vertically-centered pb-3 flex"
            style={{ height: props.gridState.gridProps.sizingConfig?.size === 'small' ? undefined : 44 }}
        >
            <AgTechGridTitle {...props} />
            <div className='column-based-verically-centered tall ml-auto flex'>
                { props.header?.content ? props.header.content() : null }
                { props.onCreate ? (
                    <button
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded hover:bg-slate-50 ml-2"
                        onClick={props.onCreate}
                    >
                        <i className="fas fa-plus text-xs" />
                        {props.gridState.gridProps.editConfig?.createButtonText ?? 'Add'}
                    </button>
                ) : null }
            </div>
            { props.search?.searchConfig ? (
                <AgTechSearchControl
                    {...props.search.searchConfig}
                    placeholder={props.search.searchConfig?.placeholder ?? 'Search...'}
                    onUpdateSearchState={props.search.onSearchStateUpdated}
                    width={200}
                    classes='ml-4'
                />
            ) : null }
        </div>
    );
}

const AgTechGridTitle = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridHeaderProps<TItem, TContextData>
) => {
    let gridTitle = props.header?.title;
    let showCounts = props.header?.showCount ?? true;

    if (props.views?.views?.views) {
        let selectedView = props.gridState.selectedView ?? props.views.views.views[0];
        gridTitle = selectedView?.name;
    }

    let itemCount = Object.values(props.gridState.entityState.allEntities).filter(ent => !(ent.state.wasCreated && !ent.state.wasWritten)).length;

    gridTitle = gridTitle ? showCounts ? `${gridTitle} (${itemCount})` : gridTitle : undefined;

    if (!gridTitle) {
        return null;
    }

    let gridTitleClasses = props.gridState.gridProps.sizingConfig?.size === 'small'
        ? 'text-sm'
        : 'centered px-6 tall text-less bg-white border rounded';

    return props.views?.views ? (
        <AgTechGridTitleWithViewSelection
            {...props}
            gridTitle={gridTitle}
            onViewSelected={props.views.onViewSelected}
        />
    ) : (
        <h1 className={`${gridTitleClasses} font-semibold flex`}>{gridTitle}</h1>
    );
}

const AgTechGridTitleWithViewSelection = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridHeaderProps<TItem, TContextData> & {
        gridTitle: string,
        onViewSelected: (selectedView: AgTechGridView<TItem>) => void
    }
) => {
    let gridViewSelectionRef = useRef(null);
    let renderedPopUp = useRef<AgTechRenderedPopUp>();

    let gridViewSelectionClickable = useClickable(gridViewSelectionRef);

    return (
        <div
            className="centered clickable pl-6 pr-4 tall text-more bg-white bordered rounded font-semibold flex"
            ref={gridViewSelectionRef}
            onClick={() => {
                if (!renderedPopUp.current) {
                    renderedPopUp.current = gridViewSelectionClickable.popups.showPopUp({
                        callerRef: gridViewSelectionRef,
                        popup: () => (
                            <AgTechGridViewSelectionPopUp
                                gridName={props.gridState.gridProps.name}
                                selectedView={props.gridState.selectedView?.name}
                                viewConfig={props.views?.views ?? { views: [] }}
                                onViewSelected={props.onViewSelected}
                            />
                        ),
                        config: {
                            position: AgTechPopUpPosition.Beside,
                            fadeInTime: '200ms'
                        },
                        handlers: {
                            onPopUpClosed: () => renderedPopUp.current = undefined
                        }
                    })
                }
            }}
        >
            {props.gridTitle}
            <i className='bi bi-chevron-down ml-4 font-size-small' style={{ marginTop: 1 }} />
        </div>
    )
}

export default AgTechGridHeader;